// src/components/PaymentSuccessModal.js
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const PaymentSuccessModal = ({
    show,
    onClose,
    paymentType, // 'membership', 'event', 'course', 'store'
    details = {}
}) => {
    const navigate = useNavigate();
    const [countdown, setCountdown] = useState(10);

    useEffect(() => {
        if (show && countdown > 0) {
            const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
            return () => clearTimeout(timer);
        } else if (countdown === 0) {
            handleClose();
        }
    }, [show, countdown]);

    const handleClose = () => {
        onClose();
        // Navigate based on payment type
        if (paymentType === 'membership') {
            navigate('/dashboard');
        } else if (paymentType === 'event') {
            navigate('/events');
        } else if (paymentType === 'course') {
            navigate('/virtual-university');
        } else if (paymentType === 'store') {
            navigate('/store');
        }
    };

    const getPaymentTypeIcon = () => {
        switch (paymentType) {
            case 'membership':
                return 'fa-user-check';
            case 'event':
                return 'fa-calendar-check';
            case 'course':
                return 'fa-graduation-cap';
            case 'store':
                return 'fa-shopping-bag';
            default:
                return 'fa-check-circle';
        }
    };

    const getPaymentTypeTitle = () => {
        switch (paymentType) {
            case 'membership':
                return 'Membership Payment Successful!';
            case 'event':
                return 'Event Registration Successful!';
            case 'course':
                return 'Course Enrollment Successful!';
            case 'store':
                return 'Order Placed Successfully!';
            default:
                return 'Payment Successful!';
        }
    };

    const getPaymentTypeMessage = () => {
        switch (paymentType) {
            case 'membership':
                return 'Your membership payment has been received. An administrator will activate your membership shortly. You will receive a confirmation email once your membership is activated.';
            case 'event':
                return 'Your event registration has been confirmed. You will receive a confirmation email with event details and joining instructions shortly.';
            case 'course':
                return 'Your course enrollment has been confirmed. You will receive access details and course materials via email shortly.';
            case 'store':
                return 'Your order has been placed successfully. You will receive an order confirmation email with tracking information shortly.';
            default:
                return 'Your payment has been processed successfully. You will receive a confirmation email shortly.';
        }
    };

    if (!show) return null;

    return (
        <>
            {/* Backdrop */}
            <div
                className="modal-backdrop show"
                style={{
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    zIndex: 1050
                }}
            />

            {/* Modal */}
            <div
                className="modal show d-block"
                tabIndex="-1"
                style={{ zIndex: 1055 }}
            >
                <div className="modal-dialog modal-dialog-centered modal-lg">
                    <div className="modal-content border-0 shadow-lg">
                        {/* Success Header with ARCA Green */}
                        <div
                            className="modal-header border-0 text-white position-relative"
                            style={{
                                background: 'linear-gradient(135deg, #6B8E23 0%, #556B2F 100%)',
                                padding: '2rem'
                            }}
                        >
                            {/* Animated Success Icon */}
                            <div className="w-100 text-center">
                                <div
                                    className="mx-auto mb-3 d-flex align-items-center justify-content-center"
                                    style={{
                                        width: '100px',
                                        height: '100px',
                                        backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                        borderRadius: '50%',
                                        animation: 'pulse 2s infinite'
                                    }}
                                >
                                    <i
                                        className={`fas ${getPaymentTypeIcon()}`}
                                        style={{
                                            fontSize: '3rem',
                                            animation: 'fadeInScale 0.5s ease-in-out'
                                        }}
                                    ></i>
                                </div>
                                <h3 className="mb-0 fw-bold">{getPaymentTypeTitle()}</h3>
                            </div>
                        </div>

                        {/* Modal Body */}
                        <div className="modal-body p-4">
                            {/* Success Message */}
                            <div className="alert alert-light border-0 mb-4" style={{ backgroundColor: '#f0f4e8' }}>
                                <div className="d-flex align-items-start">
                                    <i className="fas fa-info-circle me-3 mt-1" style={{ color: '#6B8E23', fontSize: '1.5rem' }}></i>
                                    <p className="mb-0 text-dark">{getPaymentTypeMessage()}</p>
                                </div>
                            </div>

                            {/* Payment Details */}
                            <div className="card border-0 shadow-sm mb-4" style={{ backgroundColor: '#fafafa' }}>
                                <div className="card-body p-4">
                                    <h5 className="fw-bold mb-3" style={{ color: '#6B8E23' }}>
                                        <i className="fas fa-receipt me-2"></i>Payment Details
                                    </h5>

                                    {details.name && (
                                        <div className="row mb-2">
                                            <div className="col-5 text-muted">
                                                {paymentType === 'membership' ? 'Membership Type:' :
                                                    paymentType === 'event' ? 'Event Name:' :
                                                        paymentType === 'course' ? 'Course Name:' :
                                                            'Order Type:'}
                                            </div>
                                            <div className="col-7 fw-bold text-dark">{details.name}</div>
                                        </div>
                                    )}

                                    {details.duration && (
                                        <div className="row mb-2">
                                            <div className="col-5 text-muted">Duration:</div>
                                            <div className="col-7 fw-bold text-dark">{details.duration}</div>
                                        </div>
                                    )}

                                    {details.date && (
                                        <div className="row mb-2">
                                            <div className="col-5 text-muted">Date:</div>
                                            <div className="col-7 fw-bold text-dark">{details.date}</div>
                                        </div>
                                    )}

                                    {details.amount && (
                                        <div className="row mb-2">
                                            <div className="col-5 text-muted">Amount Paid:</div>
                                            <div className="col-7 fw-bold" style={{ color: '#6B8E23', fontSize: '1.25rem' }}>
                                                ${details.amount}
                                            </div>
                                        </div>
                                    )}

                                    {details.transactionId && (
                                        <div className="row mb-2">
                                            <div className="col-5 text-muted">Transaction ID:</div>
                                            <div className="col-7 text-dark small">{details.transactionId}</div>
                                        </div>
                                    )}

                                    <div className="row">
                                        <div className="col-5 text-muted">Status:</div>
                                        <div className="col-7">
                                            <span className="badge" style={{ backgroundColor: '#6B8E23' }}>
                                                <i className="fas fa-check-circle me-1"></i>Confirmed
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Next Steps */}
                            <div className="mb-4">
                                <h6 className="fw-bold mb-3" style={{ color: '#6B8E23' }}>
                                    <i className="fas fa-clipboard-list me-2"></i>What's Next?
                                </h6>
                                <ul className="list-unstyled mb-0">
                                    <li className="mb-2 d-flex align-items-start">
                                        <i className="fas fa-envelope text-success me-3 mt-1"></i>
                                        <span className="text-dark">Check your email for a payment receipt (it may take a few minutes)</span>
                                    </li>
                                    <li className="mb-2 d-flex align-items-start">
                                        <i className="fas fa-receipt text-success me-3 mt-1"></i>
                                        <span className="text-dark">Download your receipt from your dashboard anytime</span>
                                    </li>
                                    {paymentType === 'membership' && (
                                        <li className="mb-2 d-flex align-items-start">
                                            <i className="fas fa-user-cog text-success me-3 mt-1"></i>
                                            <span className="text-dark">Admin will activate your membership within 24 hours</span>
                                        </li>
                                    )}
                                    {paymentType === 'event' && (
                                        <li className="mb-2 d-flex align-items-start">
                                            <i className="fas fa-calendar text-success me-3 mt-1"></i>
                                            <span className="text-dark">Event details and joining link will be emailed to you</span>
                                        </li>
                                    )}
                                    {paymentType === 'course' && (
                                        <li className="mb-2 d-flex align-items-start">
                                            <i className="fas fa-book-open text-success me-3 mt-1"></i>
                                            <span className="text-dark">Course access and materials will be available shortly</span>
                                        </li>
                                    )}
                                    {paymentType === 'store' && (
                                        <li className="mb-2 d-flex align-items-start">
                                            <i className="fas fa-truck text-success me-3 mt-1"></i>
                                            <span className="text-dark">Order tracking information will be sent to your email</span>
                                        </li>
                                    )}
                                    <li className="mb-2 d-flex align-items-start">
                                        <i className="fas fa-headset text-success me-3 mt-1"></i>
                                        <span className="text-dark">Contact support if you have any questions</span>
                                    </li>
                                </ul>
                            </div>

                            {/* Auto-redirect Notice */}
                            <div className="text-center">
                                <small className="text-muted">
                                    <i className="fas fa-clock me-1"></i>
                                    Redirecting in {countdown} seconds...
                                </small>
                            </div>
                        </div>

                        {/* Modal Footer */}
                        <div className="modal-footer border-0 pt-0 pb-4 px-4">
                            <button
                                type="button"
                                className="btn btn-lg w-100 text-white fw-bold"
                                style={{ backgroundColor: '#6B8E23', border: 'none' }}
                                onClick={handleClose}
                            >
                                <i className="fas fa-arrow-right me-2"></i>
                                Continue to Dashboard
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* CSS Animations */}
            <style>{`
        @keyframes pulse {
          0%, 100% {
            transform: scale(1);
            opacity: 1;
          }
          50% {
            transform: scale(1.05);
            opacity: 0.8;
          }
        }

        @keyframes fadeInScale {
          0% {
            transform: scale(0);
            opacity: 0;
          }
          50% {
            transform: scale(1.2);
          }
          100% {
            transform: scale(1);
            opacity: 1;
          }
        }

        .modal-backdrop.show {
          opacity: 1;
        }
      `}</style>
        </>
    );
};

export default PaymentSuccessModal;
